import constants from 'src/constants';
import { isAdminPage } from './general';
import { events } from './events';
import LANG_CONST from './lang/const';
import { matchPath } from 'react-router-dom';

export const permissions = {
  history: null,
  PAGES: {},
  ACTIVE_PAGE_LIST: [],
  webType: 'front',
  ACTIONS: {
    ANALYSES_ADD_BUTTON: {
      key: 'ANALYSES_ADD_BUTTON',
      checkActive: ({ auth }) => !!auth && auth.isPatient && !isAdminPage()
    },
    TREATMENTS_ADD_BUTTON: {
      key: 'TREATMENTS_ADD_BUTTON',
      checkActive: ({ auth }) =>
        !!auth &&
        auth.isDoctor &&
        !isAdminPage() &&
        auth.status !== constants.general.userStatus.disabled
    },
    TREATMENTS_REPLY_BUTTON: {
      key: 'TREATMENTS_REPLY_BUTTON',
      checkActive: ({ auth }) =>
        !!auth &&
        auth.isDoctor &&
        !isAdminPage() &&
        auth.status !== constants.general.userStatus.disabled
    },
    TREATMENTS_FORWARD_BUTTON: {
      key: 'TREATMENTS_FORWARD_BUTTON',
      checkActive: ({ auth }) => !!auth && auth.isPatient && !isAdminPage()
    },
    TREATMENTS_FEEDBACK_BUTTON: {
      key: 'TREATMENTS_FEEDBACK_BUTTON',
      checkActive: ({ auth }) => !!auth && auth.isPatient && !isAdminPage()
    },
    JOURNEY_ADD_BUTTON: {
      key: 'JOURNEY_ADD_BUTTON',
      checkActive: ({ auth }) => !!auth && auth.isPatient && !isAdminPage()
    },
    ENTRY_ADD_BUTTON: {
      key: 'ENTRY_ADD_BUTTON',
      checkActive: ({ auth }) => !!auth && auth.isPatient && !isAdminPage()
    },
    INVOICE_ADD_BUTTON: {
      key: 'INVOICE_ADD_BUTTON',
      checkActive: ({ auth }) =>
        !!auth &&
        auth.isDoctor &&
        !isAdminPage() &&
        auth.status !== constants.general.userStatus.disabled
    },
    SETTING_MANAGER_ADD_BUTTON: {
      key: 'SETTING_MANAGER_ADD_BUTTON',
      checkActive: ({ auth }) =>
        !!auth &&
        (auth.managerAdmin || auth.isClinic) &&
        auth.status !== constants.general.userStatus.disabled
    },
    SETTING_MANAGER_EDIT_BUTTON: {
      key: 'SETTING_MANAGER_EDIT_BUTTON',
      checkActive: ({ auth }) =>
        !!auth &&
        (auth.isAdmin || auth.isClinic || auth.managerAdmin) &&
        auth.status !== constants.general.userStatus.disabled
    },
    SLOT_ADD_BUTTON: {
      key: 'SLOT_ADD_BUTTON',
      checkActive: ({ auth }) =>
        !!auth &&
        (auth.isDoctor || auth.isClinic || auth.isManager) &&
        auth.status !== constants.general.userStatus.disabled
    },
    APPOINTMENTS_BUTTON: {
      buttons: ({ auth, appointment, doctor }) => {
        if (!appointment || !appointment.data) return [];
        let doctorProfile = doctor || appointment.doctor;

        const isOwner =
          auth?.isAdmin ||
          appointment?.data?.clinicId === auth?.id ||
          appointment?.from === auth?.id ||
          appointment?.to === auth?.id;
        const isAdmin = !!auth.managerAdmin && auth?.managerToClinic === appointment?.clinicId;
        const hospitalIds =
          auth.managerAccess?.filter((a) => !!a.appointments).map((a) => a.hospitalId) || [];

        let canEdit =
          !!appointment &&
          !!auth &&
          appointment.data.status !== constants.general.appointmentStatus.cancelled &&
          auth.status !== constants.general.userStatus.disabled &&
          (!doctorProfile || doctorProfile.status !== constants.general.userStatus.disabled) &&
          (auth?.isPatient ||
            isOwner ||
            isAdmin ||
            (appointment?.data?.appointmentType === 'virtual' && hospitalIds.length > 0) ||
            hospitalIds?.includes(appointment?.data?.hospitalId));

        let canAdd =
          !!auth &&
          auth.status !== constants.general.userStatus.disabled &&
          (!doctorProfile || doctorProfile.status !== constants.general.userStatus.disabled) &&
          (!auth.isManager || isAdmin || hospitalIds.length > 0);

        const actions = [];
        const startDate = appointment.data.startDate;
        const now = Date.now();
        const isDoctor = auth.isDoctor && !auth.isAdmin;

        if (canEdit && isDoctor) {
          actions.push('APPOINTMENTS_CONFIRM_BUTTON');
          actions.push('APPOINTMENTS_RESCHEDULE_BUTTON');
          actions.push('APPOINTMENTS_CANCEL_BUTTON');

          if (appointment?.data?.appointmentType === 'virtual')
            actions.push('APPOINTMENTS_CONTINUE_BUTTON');
        }

        if (
          canEdit &&
          now <= startDate &&
          appointment.data.status === constants.general.appointmentStatus.upcoming
        ) {
          actions.push('APPOINTMENTS_RESCHEDULE_BUTTON');
          actions.push('APPOINTMENTS_CANCEL_BUTTON');
        }

        if (canEdit && now <= startDate) actions.push('APPOINTMENTS_EDIT_BUTTON');

        if (!actions.length && canAdd) actions.push('APPOINTMENTS_REBOOK_BUTTON');

        return actions;
      }
    },
    APPOINTMENTS_EDIT_BUTTON: {
      attributes: { label: LANG_CONST.buttons.edit, primary: true, dark: true, radius: true },
      checkActive: function (params) {
        return permissions.ACTIONS.APPOINTMENTS_BUTTON.buttons(params).includes(this.key);
      }
    },
    APPOINTMENTS_RESCHEDULE_BUTTON: {
      attributes: {
        label: LANG_CONST.buttons.reschedule,
        primary: true,
        light: true,
        radius: true
      },
      checkActive: function (params) {
        return permissions.ACTIONS.APPOINTMENTS_BUTTON.buttons(params).includes(this.key);
      }
    },
    APPOINTMENTS_CONTINUE_BUTTON: {
      attributes: {
        label: LANG_CONST.buttons.chat_video,
        primary: true,
        light: true,
        radius: true
      },
      checkActive: function (params) {
        return permissions.ACTIONS.APPOINTMENTS_BUTTON.buttons(params).includes(this.key);
      }
    },
    APPOINTMENTS_CONFIRM_BUTTON: {
      attributes: { label: LANG_CONST.buttons.confirm, primary: true, light: true, radius: true },
      checkActive: function (params) {
        return permissions.ACTIONS.APPOINTMENTS_BUTTON.buttons(params).includes(this.key);
      }
    },
    APPOINTMENTS_REBOOK_BUTTON: {
      attributes: { label: LANG_CONST.buttons.rebook, primary: true, light: true, radius: true },
      checkActive: function (params) {
        return permissions.ACTIONS.APPOINTMENTS_BUTTON.buttons(params).includes(this.key);
      }
    },
    APPOINTMENTS_CANCEL_BUTTON: {
      attributes: { label: LANG_CONST.buttons.cancel, quaternary: true, light: true, radius: true },
      checkActive: function (params) {
        return permissions.ACTIONS.APPOINTMENTS_BUTTON.buttons(params).includes(this.key);
      }
    },
    SETTING_CHARGE_ADD_BUTTON: {
      key: 'SETTING_CHARGE_ADD_BUTTON',
      checkActive: ({ auth }) => !!auth && ((auth.isDoctor && !auth.createdBy) || auth.isClinic)
    }
  },
  addPages: function (pages, defaultOptions = {}) {
    for (let k in pages) {
      if (this.PAGES[k]) {
        throw new Error('Page is exist.');
      }
      this.PAGES[k] = { ...defaultOptions, ...pages[k], key: k };
    }
  },
  switchToAdmin: function (reLoad = false) {
    this.webType = 'admin';
    if (reLoad) events.emit('rerender');
  },
  switchToFront: function (reLoad = false) {
    this.webType = 'front';
    if (reLoad) events.emit('rerender');
  },
  run: function (params) {
    this.ACTIVE_PAGE_LIST = [];
    for (let k in this.PAGES) {
      const find = this.PAGES[k];
      if (find.webType !== 'public' && find.webType !== this.webType) continue;
      find.isActive =
        (!find.defaultCheckActive ||
          find.defaultCheckActive({ ...params, pages: this.PAGES, page: find })) &&
        (!find.checkActive || find.checkActive({ ...params, pages: this.PAGES, page: find }));

      if (find.isActive) {
        this.ACTIVE_PAGE_LIST.push(find);
      }
    }

    for (let k in this.ACTIONS) {
      if (!this.ACTIONS[k].key) this.ACTIONS[k].key = k;
      this.ACTIONS[k].isActive =
        !this.ACTIONS[k].checkActive ||
        this.ACTIONS[k].checkActive({ ...params, page: this.ACTIONS[k] });
    }
  },
  profilePath: function (user) {
    if (isAdminPage())
      return user?.isDoctor
        ? this.PAGES.ADMIN_DOCTORS_DETAIL_PAGE.match(user?.username)
        : user?.isClinic
          ? this.PAGES.PROFILE_PAGE.match(user?.username)
          : this.PAGES.ADMIN_PATIENTS_DETAIL_PAGE.match(user?.id);
    return user?.isDoctor
      ? this.PAGES.DOCTORS_DETAIL_PAGE.match(user?.username)
      : user?.isClinic
        ? this.PAGES.PROFILE_PAGE.match(user?.username)
        : this.PAGES.PATIENTS_DETAIL_PAGE.match(user?.id);
  },
  hasCompletedProfileUri: function (user) {
    if (!user) return false;
    if (user?.isDoctor) return;

    const completed =
      user?.isDoctor || user.isClinic
        ? !!user.specialist && !!user.specialist.length
        : !!user.birthday;

    if (!completed) {
      return user?.isDoctor
        ? this.PAGES.SIGNUP_DOCTOR_PAGE.match()
        : user?.isClinic
          ? this.PAGES.SIGNUP_CLINIC_PAGE.match()
          : this.PAGES.SIGNUP_PAGE.match();
    }

    return null;
  },
  afterLogin: function (user) {
    let redirect = permissions.hasCompletedProfileUri(user);
    if (redirect)
      permissions.history = {
        url: redirect,
        params: { isRedirect: true }
      };
    // if (!redirect) permissions.showCertificatePopup(user);
    if (!redirect) {
      console.log('afterLogin showSignupPopup');
      events.emit('showSignupPopup');
      // permissions.showSignupPopupIfNeeded(user);
    }
  },
  findMatchPath: function (pathname) {
    for (const key in this.PAGES) {
      const route = this.PAGES[key];
      const match = matchPath(route.path, pathname);
      if (match) {
        return route;
      }
    }
    return null;
  }
};
